import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useLatestEditAreaQuery } from '@/entities/edit-area';
import { useInvalidateProducts, useProductDetailQuery } from '@/entities/product';
import {
  BasicInfoForm,
  CategorySection,
  ColorSection,
  EditAreaSection,
  FontAllowSection,
  ImageSection,
  InkColorAllowSection,
  PriceSection,
  PrintMethodSection,
  PublishPanel,
  SizeSection,
  sectionKeysOf,
  useUpdateBasicInfo,
} from '@/features/catalog';
import { SETUP_STEPS } from '../model/setup-steps';
import { ProductSetupSteps } from './product-setup-steps';

const SAVED_TOAST_MS = 2500;

/** 등록 순서 단계의 섹션 앵커 id. 앵커로 스크롤될 때 위쪽에 여백을 남긴다. */
const anchorOf = (key: (typeof SETUP_STEPS)[number]['key']): string =>
  SETUP_STEPS.find((step) => step.key === key)?.anchorId ?? '';
const ANCHOR_CLASS = 'flex scroll-mt-6 flex-col gap-6';

interface EditProductViewProps {
  readonly productId: string;
}

/** 수정 화면 — 섹션별 저장 파이프라인을 조합하고 저장 토스트를 소유한다. */
export const EditProductView = ({ productId }: EditProductViewProps): ReactElement => {
  const invalidate = useInvalidateProducts();
  const detailQuery = useProductDetailQuery(productId);
  // 등록 순서의 "편집영역" 완료 여부 — 공개 패널의 발행 조건과 같은 조회(캐시 공유).
  const editAreaQuery = useLatestEditAreaQuery(productId);

  // 섹션 저장 성공 토스트 — 섹션이 invalidate 로 리마운트돼도 메시지가 살아남도록
  // 페이지가 소유한다.
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const savedTimerRef = useRef<number | null>(null);

  const handleSaved = (message: string): void => {
    setSavedMessage(message);

    if (savedTimerRef.current !== null) {
      window.clearTimeout(savedTimerRef.current);
    }

    savedTimerRef.current = window.setTimeout(() => setSavedMessage(null), SAVED_TOAST_MS);
  };

  useEffect(
    () => () => {
      if (savedTimerRef.current !== null) {
        window.clearTimeout(savedTimerRef.current);
      }
    },
    [],
  );

  const updateAction = useUpdateBasicInfo({
    onSuccess: () => {
      handleSaved('기본 정보를 저장했습니다.');
      invalidate.detail(productId);
      invalidate.list();
    },
  });

  if (detailQuery.isPending) {
    return <p className="text-muted-foreground">불러오는 중…</p>;
  }

  if (detailQuery.isError && detailQuery.data === undefined) {
    return (
      <p role="alert" className="text-destructive">
        상품을 불러오지 못했습니다: {detailQuery.error.message}
      </p>
    );
  }

  const detail = detailQuery.data;
  // 저장 후 refetch 되면 "저장된 그 섹션만" 최신 서버 상태로 재초기화한다 — 섹션별 키.
  // 키를 하나로 공유하면 다른 섹션에서 입력 중이던 값이 리마운트로 사라진다.
  const keys = sectionKeysOf(detail);
  // 등록 순서 단계별 완료 — 수정 화면에 왔다면 기본 정보는 이미 저장된 상태다.
  const stepsDone = [
    true,
    detail.categories.length > 0 && detail.colorOptions.length > 0,
    detail.printMethodIds.length > 0 && detail.images.length > 0,
    detail.priceTiers.length > 0,
    Boolean(editAreaQuery.data),
    detail.status === 'PUBLISHED',
  ];

  return (
    <>
      {savedMessage ? (
        <p
          role="status"
          className="fixed right-6 bottom-6 z-50 rounded-full bg-primary px-5 py-2.5 text-sm text-white shadow-lg"
        >
          <span className="text-brand-lime">✓</span> {savedMessage}
        </p>
      ) : null}

      {/* 넓은 화면은 두 칸 — 왼쪽 섹션들, 오른쪽 등록 순서(화면에 붙고 단계를 누르면 그 섹션으로 간다). */}
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-6">
          {detailQuery.isError ? (
            <p role="alert" className="text-destructive">
              상품 정보 갱신에 실패했습니다. 현재 편집은 유지됩니다.
            </p>
          ) : null}
          <div id={anchorOf('publish')} className={ANCHOR_CLASS}>
            <PublishPanel
              productId={productId}
              productName={detail.name}
              status={detail.status}
              hasCategories={detail.categories.length > 0}
              hasPriceTiers={detail.priceTiers.length > 0}
              hasMeasurement={detail.widthMm !== null && detail.heightMm !== null}
            />
          </div>

          <section
            id={anchorOf('basic')}
            className="scroll-mt-6 rounded-2xl border border-foreground/[0.06] bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
          >
            <h2 className="text-lg font-semibold tracking-[-0.01em]">기본 정보</h2>
            {updateAction.error ? (
              <p role="alert" className="mt-2 text-sm text-destructive">
                {updateAction.error.message}
              </p>
            ) : null}
            <div className="mt-4">
              <BasicInfoForm
                key={keys.basic}
                defaultValues={{
                  name: detail.name,
                  minOrderQty: detail.minOrderQty,
                  leadTimeNote: detail.leadTimeNote,
                  materialNote: detail.materialNote,
                  measurementKind: detail.measurementKind,
                  widthMm: detail.widthMm ?? undefined,
                  heightMm: detail.heightMm ?? undefined,
                  productionNote: detail.productionNote,
                }}
                submitLabel="기본 정보 저장"
                isSubmitting={updateAction.isPending}
                onSubmit={(values) => updateAction.updateBasicInfo({ id: productId, ...values })}
              />
            </div>
          </section>

          <div id={anchorOf('category')} className={ANCHOR_CLASS}>
            <CategorySection
              key={keys.category}
              productId={productId}
              links={detail.categories}
              onSaved={handleSaved}
            />
            <ColorSection
              key={keys.color}
              productId={productId}
              colorOptions={detail.colorOptions}
              onSaved={handleSaved}
            />
            <SizeSection
              key={keys.size}
              productId={productId}
              sizeOptions={detail.sizeOptions}
              onSaved={handleSaved}
            />
          </div>
          <div id={anchorOf('print')} className={ANCHOR_CLASS}>
            <PrintMethodSection
              key={keys.print}
              productId={productId}
              printMethodIds={detail.printMethodIds}
              onSaved={handleSaved}
            />
            <ImageSection
              key={keys.image}
              productId={productId}
              images={detail.images}
              detailImage={detail.detailImage}
              onSaved={handleSaved}
            />
          </div>
          <div id={anchorOf('price')} className={ANCHOR_CLASS}>
            <PriceSection
              key={keys.price}
              productId={productId}
              minOrderQty={detail.minOrderQty}
              priceTiers={detail.priceTiers}
              onSaved={handleSaved}
            />
          </div>
          <div id={anchorOf('editArea')} className={ANCHOR_CLASS}>
            <EditAreaSection productId={productId} />
          </div>
          {/* 고객 에디터 설정 — 상품 상세에 허용 목록이 없어 섹션이 따로 불러오고 리마운트 키도 스스로 정한다. */}
          <FontAllowSection productId={productId} onSaved={handleSaved} />
          <InkColorAllowSection productId={productId} onSaved={handleSaved} />
        </div>

        <ProductSetupSteps done={stepsDone} linkSections />
      </div>
    </>
  );
};
